import React, { useMemo } from 'react';

import { twMerge } from 'tailwind-merge';
import { ArtistDrop } from '@components/Feed/Activities/Types/ArtistDrop';
import { FeedActivityTypePost } from '@components/Feed/Activities/Types/Post';
import { ReleaseCollectedByMany } from '@components/Feed/Activities/Types/ReleaseCollectedByMany';
import { UserCollectedManySongs } from '@components/Feed/Activities/Types/UserCollectedManySongs';
import {
  FeedActivityTypePostFragmentDoc,
  FeedPostActionsInfoFragmentDoc,
  getFragment,
} from '@graphql/reactQuery';
import { Divider } from './Divider';
import { PostRepliesPreview } from './Post/Comments/PostRepliesPreview';
import { ActivityFeedState, type ActivityGroupWithDivider } from './store';

export const FeedActivityGroup = ({
  item,
  index,
}: {
  index: number;
  item: ActivityGroupWithDivider;
}) => {
  const information = item.information;
  const timeStamp = item.latestActivityOccurenceAt;

  const showCommentsPreview =
    information.__typename === 'UserCreatedPostAggregate' ||
    information.__typename === 'ReleaseDroppedAggregate' ||
    information.__typename === 'UserCollectedManySongsAggregate';

  const content = useMemo(() => {
    switch (information.__typename) {
      case 'SongCollectedByManyAggregate':
        return (
          <ReleaseCollectedByMany
            key={item.id}
            activityId={item.id}
            activityFrag={information}
            timeStamp={timeStamp}
          />
        );
      case 'ReleaseDroppedAggregate':
        const artistDropPost = getFragment(FeedPostActionsInfoFragmentDoc, information.post);

        return (
          <>
            <ArtistDrop
              key={item.id}
              releaseFrag={information.release}
              postActionsFrag={information.post}
              timeStamp={timeStamp}
            />

            {!!artistDropPost && (
              <PostRepliesPreview
                postId={artistDropPost.id}
                postWebappUri={artistDropPost.webappUri}
                postCommentCount={artistDropPost.commentCount}
              />
            )}
          </>
        );
      case 'UserCollectedManySongsAggregate':
        const collectedManyPost = getFragment(FeedPostActionsInfoFragmentDoc, information.post);

        return (
          <>
            <UserCollectedManySongs
              key={item.id}
              activityFrag={information}
              postActionsFrag={information.post}
              timeStamp={item.latestActivityOccurenceAt}
            />
            {!!collectedManyPost && (
              <PostRepliesPreview
                postId={collectedManyPost.id}
                postWebappUri={collectedManyPost.webappUri}
                postCommentCount={collectedManyPost.commentCount}
              />
            )}
          </>
        );

      case 'UserCreatedPostAggregate':
        const { post: userPost } = getFragment(FeedActivityTypePostFragmentDoc, information);

        return (
          <>
            <FeedActivityTypePost key={item.id} activityFrag={information} timeStamp={timeStamp} />
            {!!userPost &&
              (userPost.__typename === 'Repost' ? (
                !!userPost.originalPost && (
                  <PostRepliesPreview
                    postId={userPost.originalPost.id}
                    postWebappUri={userPost.originalPost.webappUri}
                    postCommentCount={userPost.originalPost.commentCount}
                  />
                )
              ) : (
                <PostRepliesPreview
                  postId={userPost.id}
                  postWebappUri={userPost.webappUri}
                  postCommentCount={userPost.commentCount}
                />
              ))}
          </>
        );

      default:
        return null;
    }
  }, [item, information, timeStamp]);

  return (
    <div
      className="pb-5 md:pb-0"
      onClick={e => {
        ActivityFeedState.scrollIndex = index;
        ActivityFeedState.positionFromTop = e.currentTarget.getBoundingClientRect().top;
      }}
    >
      {content}

      <div className={twMerge(!showCommentsPreview && 'pt-5 md:pt-0')}>
        <Divider />
      </div>
    </div>
  );
};
